import React from "react";
import { Box, LinearProgress, Paper, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Skeleton from "@material-ui/lab/Skeleton";

const useStyles = makeStyles((theme) => ({
  barOk: {
    backgroundColor: theme.palette.primary.main,
  },
  barWarn: {
    backgroundColor: "#ed6c02",
  },
  barOver: {
    backgroundColor: theme.palette.error.main,
  },
  track: {
    height: 8,
    borderRadius: 6,
    backgroundColor: theme.palette.action.hover,
  },
}));

const meterPaperStyle = { padding: 16, marginBottom: 16 };

const money = (value) => {
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  return safe.toLocaleString(undefined, { style: "currency", currency: "USD" });
};

const tokens = (value) => {
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  const sign = safe < 0 ? "-" : "";
  const abs = Math.abs(safe);
  if (abs < 1000) return `${sign}${Math.round(abs)}`;
  if (abs < 1000000) return `${sign}${(abs / 1000).toFixed(1)}k`;
  if (abs < 1000000000) return `${sign}${(abs / 1000000).toFixed(2)}M`;
  return `${sign}${(abs / 1000000000).toFixed(2)}B`;
};

export const AgentAiCreditMeterSkeleton = () => (
  <Paper variant="outlined" style={meterPaperStyle} aria-busy="true" aria-label="Loading AI credit">
    <Box display="flex" justifyContent="space-between" alignItems="baseline">
      <Skeleton animation="wave" variant="text" width={120} height={24} />
      <Skeleton animation="wave" variant="text" width={90} height={24} />
    </Box>
    <Box mt={1} mb={1}>
      <Skeleton animation="wave" variant="rect" height={8} style={{ borderRadius: 6 }} />
    </Box>
    <Skeleton animation="wave" variant="text" width="80%" height={20} />
  </Paper>
);

const AgentAiCreditMeter = ({ usage }) => {
  const classes = useStyles();
  const credit = Number(usage?.credit_usd);
  const allowance = Number.isFinite(credit) && credit > 0 ? credit : 15;
  const spent = Number(usage?.spent_usd) || 0;
  const overage = Number(usage?.overage_usd) || Math.max(0, spent - allowance);
  const pctRaw = allowance > 0 ? (Math.min(spent, allowance) / allowance) * 100 : 0;
  const pct = Math.min(100, spent > 0 ? Math.max(2, pctRaw) : 0);
  const barClass = overage > 0 ? classes.barOver : pctRaw >= 80 ? classes.barWarn : classes.barOk;
  const cacheRead = Number(usage?.cache_read_tokens) || 0;
  const cacheWrite = Number(usage?.cache_write_tokens) || 0;
  const cacheNote =
    cacheRead > 0 || cacheWrite > 0
      ? ` Includes cache read ${tokens(cacheRead)} and cache write ${tokens(cacheWrite)}.`
      : "";

  return (
    <Paper variant="outlined" style={meterPaperStyle}>
      <Box display="flex" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" style={{ gap: 8 }}>
        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
          AI credit
        </Typography>
        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
          {money(spent)} / {money(allowance)}
        </Typography>
      </Box>
      <Box mt={1} mb={1}>
        <LinearProgress
          variant="determinate"
          value={pct}
          className={classes.track}
          classes={{ bar: barClass }}
        />
      </Box>
      <Typography variant="caption" color="textSecondary" component="div">
        {overage > 0
          ? `${money(overage)} over the monthly credit. Overages are billed weekly when a card is on file, or invoiced monthly otherwise.`
          : `${money(Math.max(0, allowance - spent))} of this month's AI credit remaining. The credit resets each month, including on the free plan.`}
      </Typography>
      <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: 4 }}>
        Input {tokens(usage?.input_tokens)} · Output {tokens(usage?.output_tokens)} · Total{" "}
        {tokens(usage?.total_tokens)}
        {cacheNote}
      </Typography>
    </Paper>
  );
};

export default AgentAiCreditMeter;
